import React from 'react';
import { X, Sparkles, MessageCircle, BookOpen, History, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { getLocalizedPrice } from '../lib/pricing';

interface LifetimeAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgradeClick: () => void;
  isUpgrading?: boolean;
}

const LifetimeAccessModal: React.FC<LifetimeAccessModalProps> = ({
  isOpen,
  onClose,
  onUpgradeClick,
  isUpgrading = false,
}) => {
  if (!isOpen) return null;

  const { symbol, amount } = getLocalizedPrice();

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-start justify-center pt-8 px-4 pb-20 z-50 backdrop-blur-sm"
    >
      <motion.div 
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 relative"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="flex justify-center mb-6">
          <div className="h-20 w-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center">
            <Sparkles className="h-10 w-10 text-purple-600" />
          </div>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold mb-2">
            Unlock Lifetime Access
          </h1>
          
          <p className="text-gray-600">
            Get unlimited access to all features, forever.
          </p>

          <div className="mt-4 inline-flex items-baseline justify-center">
            <span className="text-4xl font-bold text-gray-900">{symbol}{amount}</span>
            <span className="text-gray-500 ml-2">one-time payment</span>
          </div>
        </div>

        <div className="space-y-4 mb-8">
          <div className="flex items-start">
            <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="h-4 w-4 text-purple-600" />
            </div>
            <span className="text-gray-700 ml-3">
              Unlimited conversations with Echo
            </span>
          </div>
          
          <div className="flex items-start">
            <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="h-4 w-4 text-purple-600" />
            </div>
            <span className="text-gray-700 ml-3">
              Personal reflections after each chat
            </span>
          </div>
          
          <div className="flex items-start">
            <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="h-4 w-4 text-purple-600" />
            </div>
            <span className="text-gray-700 ml-3">
              Complete conversation history
            </span>
          </div>

          <div className="flex items-start">
            <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="h-4 w-4 text-purple-600" />
            </div>
            <span className="text-gray-700 ml-3">
              One-time payment, lifetime access
            </span>
          </div>
        </div>

        <button
          onClick={onUpgradeClick}
          disabled={isUpgrading}
          className="w-full py-3 bg-gradient-to-r from-[#a85ff7] to-[#e457b5] text-white rounded-lg hover:opacity-90 transition-opacity font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUpgrading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
              Loading...
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5 mr-2" />
              Unlock Unlimited Conversations
            </>
          )}
        </button>

        <p className="text-sm text-gray-500 text-center mt-4">
          Secure payment with Stripe
        </p>
      </motion.div>
    </motion.div>
  );
};

export default LifetimeAccessModal;